import {
  baseColours,
  getHslaColor,
  getHeatmapColor,
  strokeColours,
  HeatmapConfig,
} from './baseColours';

import { Fill, Style, Circle, Stroke, Text, Icon } from 'ol/style';
import { Circle as CircleGeom } from 'ol/geom';
import { FeatureLike } from 'ol/Feature';
import { Feature } from 'ol';

type PointSize = 'small' | 'medium' | 'large';
type LineWidth = 'thin' | 'medium' | 'thick';

export type StyleFunction = (feature: Feature, resolution: number) => Style;

const addLabelToStyle = (style: Style, feature: FeatureLike) => {
  const labelText = feature.get('name') || '';
  const labelStyle = getLabelStyle();
  style.setText(labelStyle);
  style.getText()?.setText(labelText ?? '');

  return style;
};

export const getPointStyle = (
  colour: keyof typeof baseColours,
  size: PointSize,
  opacity = 1
): Style => {
  const pointSizes = {
    small: 3,
    medium: 5,
    large: 8,
  };

  const pointStyle = new Style({
    image: new Circle({
      radius: pointSizes[size],
      stroke: new Stroke({
        color: getHslaColor(strokeColours[colour], 1.0),
        width: 2,
      }),
      fill: new Fill({
        color: getHslaColor(baseColours[colour], opacity),
      }),
    }),
  });

  return pointStyle;
};

export const getLineStyle = (
  colour: keyof typeof baseColours,
  width: LineWidth,
  opacity = 1
): Style => {
  const lineWidths = {
    thin: 2,
    medium: 4,
    thick: 6,
  };

  const lineStyle = new Style({
    stroke: new Stroke({
      color: getHslaColor(baseColours[colour], 1.0),
      width: lineWidths[width],
    }),
  });

  return lineStyle;
};

export const getPolygonStyle = (
  colour: keyof typeof baseColours,
  strokeWidth: LineWidth,
  opacity = 1
): Style => {
  const lineWidths = {
    thin: 2,
    medium: 4,
    thick: 6,
  };

  const polygonStyle = new Style({
    stroke: new Stroke({
      color: getHslaColor(strokeColours[colour], 1.0),
      width: lineWidths[strokeWidth],
    }),
    fill: new Fill({
      color: getHslaColor(baseColours[colour], opacity),
    }),
  });

  return polygonStyle;
};

export const getCircleStyle = (
  colour: keyof typeof baseColours,
  strokeWidth: LineWidth,
  radius: number,
  showLabel: boolean,
  feature: FeatureLike,
  opacity = 1
): Style => {
  const lineWidths = {
    thin: 2,
    medium: 4,
    thick: 6,
  };

  return new Style({
    renderer: (coordinates, state) => {
      const [[x, y], [x1, y1]] = coordinates as [
        [number, number],
        [number, number]
      ];

      const ctx = state.context;
      const dx = x1 - x;
      const dy = y1 - y;
      const calculatedRadius = Math.sqrt(dx * dx + dy * dy);

      ctx.beginPath();
      ctx.arc(x, y, calculatedRadius, 0, 2 * Math.PI, true);
      ctx.fillStyle = getHslaColor(baseColours[colour], opacity);
      ctx.fill();

      ctx.lineWidth = lineWidths[strokeWidth];
      ctx.strokeStyle = getHslaColor(strokeColours[colour], 1.0);
      ctx.stroke();

      if (showLabel) {
        const labelText = feature?.get('name') || '';
        ctx.font = '14px Roboto, sans-serif';
        ctx.fillStyle = '#333333';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(labelText, x, y - calculatedRadius - 12); // Adjust the position of the label
      }
    },
  });
};

export const getLabelStyle = (): Text => {
  const labelStyle = new Text({
    font: '14px Roboto,sans-serif',
    overflow: true,
    fill: new Fill({
      color: '#333333', // Dark Gray
    }),
    stroke: new Stroke({
      color: '#ffffff', // White outline to ensure readability
      width: 1,
    }),
    offsetY: -12,
  });

  return labelStyle;
};

export const getGpsLostHexagonStyle = (
  feature: FeatureLike,
  heatmapConfig: HeatmapConfig | null | undefined,
  isSelected = false,
  showLabel = false
): Style => {
  const gpsLostValue = feature.get('ships_w_lost_gps') || 0;

  // Base color depends on selection state
  const color = isSelected
    ? getHeatmapColor(gpsLostValue, heatmapConfig, 1)
    : getHeatmapColor(gpsLostValue, heatmapConfig, 0.6);

  // Stroke also depends on selection state
  const strokeColor = 'rgba(0, 0, 0, 0.5)';
  const strokeWidth = isSelected ? 3 : 0.5;

  const style = new Style({
    fill: new Fill({ color }),
    stroke: new Stroke({
      color: strokeColor,
      width: strokeWidth,
    }),
  });

  // Add label if requested
  if (showLabel && gpsLostValue > 0) {
    style.setText(
      new Text({
        text: gpsLostValue.toString(),
        font: '12px Roboto, sans-serif',
        fill: new Fill({
          color: '#000000',
        }),
        stroke: new Stroke({
          color: '#FFFFFF',
          width: 3,
        }),
      })
    );
  }

  return style;
};

export const getGPSLostTrackLineStyle = (
  feature: FeatureLike,
  showLabel = false
): Style => {
  const hasGpsLost = feature.get('hasGpsLost') || false;

  const style = new Style({
    stroke: new Stroke({
      color: hasGpsLost ? 'rgba(255, 0, 0, 0.7)' : 'rgba(0, 91, 187, 0.7)', // Red for GPS lost, blue otherwise
      width: 2,
      lineDash: hasGpsLost ? [5, 5] : undefined,
    }),
  });

  // Add vessel name as label if requested
  if (showLabel) {
    const name = feature.get('name') || feature.get('mmsi') || '';
    if (name) {
      const text = new Text({
        text: name,
        font: '12px Roboto, sans-serif',
        fill: new Fill({
          color: '#000000',
        }),
        stroke: new Stroke({
          color: '#ffffff',
          width: 3,
        }),
        offsetY: -15,
        placement: 'line', // Place label along the line
      });
      style.setText(text);
    }
  }

  return style;
};

export const getGPSLostTrackPointStyle = (
  feature: FeatureLike,
  showLabel = false,
  timezone?: string
): Style => {
  const isGpsLost = feature.get('isGpsLost') || false;
  const isFirstPoint = feature.get('isFirstPoint') || false;
  const isLastPoint = feature.get('isLastPoint') || false;
  let pointColor: 'red' | 'blue';

  // Determine color based on point properties
  if (isGpsLost) {
    pointColor = 'red'; // Red for GPS lost points
  } else {
    pointColor = 'blue'; // Blue for regular points
  }

  // Determine size based on point importance
  const pointSize = isGpsLost ? 'large' : 'small';
  const style = getPointStyle(pointColor, pointSize);

  // Add label if requested
  if (showLabel) {
    let labelText = '';
    if (feature.get('timestamp')) {
      const date = feature.get('timestamp');

      // Format the date with the specified timezone, including date and month, using 24-hour format
      labelText = new Intl.DateTimeFormat('default', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false, // Use 24-hour format
        timeZone: timezone || 'UTC',
      }).format(date);
    }

    if (labelText) {
      const text = new Text({
        text: labelText,
        font: '11px Roboto, sans-serif',
        fill: new Fill({
          color: '#000000',
        }),
        stroke: new Stroke({
          color: '#ffffff',
          width: 3,
        }),
        offsetY: -12,
      });
      style.setText(text);
    }
  }

  return style;
};

export const getFeatureStyle = (
  feature: FeatureLike,
  colour: keyof typeof baseColours,
  pointSize: PointSize,
  width: LineWidth,
  showLabel: boolean,
  transparency = 0,
  timezone?: string,
  heatmapConfig?: HeatmapConfig
): Style => {
  const geometry = feature.getGeometry();
  const opacity = 1 - transparency; // Invert the transparency value to get the opacity

  let style: Style;

  const geometryType = geometry?.getType();

  if (feature.get('type') === 'port') {
    const size = feature.get('size') || 'medium';
    style = getPortStyle(size);
  } else if (feature.get('type') === 'source-identifier') {
    const size = feature.get('size') || 'medium';
    style = getSourceIdentifierStyle(size);
  } else if (feature.get('type') === 'gps-lost') {
    return getGpsLostHexagonStyle(feature, heatmapConfig, false, showLabel);
  } else if (feature.get('type') === 'gps-lost-track') {
    return getGPSLostTrackLineStyle(feature, showLabel);
  } else if (feature.get('type') === 'gps-lost-track-point') {
    return getGPSLostTrackPointStyle(feature, showLabel, timezone);
  } else if (geometryType === 'Point') {
    style = getPointStyle(colour, pointSize, opacity);
  } else if (geometryType === 'LineString') {
    style = getLineStyle(colour, width, opacity);
  } else if (geometryType === 'Polygon') {
    style = getPolygonStyle(colour, width, opacity);
  } else if (geometryType === 'Circle') {
    const radius = (geometry as CircleGeom).getRadius();
    style = getCircleStyle(colour, width, radius, showLabel, feature, opacity);
  } else {
    console.log('FALLBACK');
    style = new Style(); // Fallback style if geometry type is not recognized
  }

  if (showLabel) {
    return addLabelToStyle(style, feature);
  }

  return style;
};

export const getVirtualAtonStyle = () => {
  return new Style({
    image: new Icon({
      src: 'virtual_aton.png',
      scale: 0.3,
      anchor: [0.5, 0.5],
    }),
  });
};

export const getAtonStyle = () => {
  return new Style({
    image: new Icon({
      src: 'aton.png',
      scale: 0.3,
      anchor: [0.5, 0.5],
    }),
  });
};

export const getPortStyle = (size: string) => {
  let scale = 0.2;
  if (size === 'small') {
    scale = 0.15;
  } else if (size === 'medium') {
    scale = 0.2;
  } else if (size === 'large') {
    scale = 0.3;
  }

  return new Style({
    image: new Icon({
      src: 'anchor.png',
      scale: scale,
      anchor: [0.5, 0.5],
    }),
  });
};

export const getSourceIdentifierStyle = (size: string) => {
  let scale = 0.2;
  if (size === 'small') {
    scale = 0.15;
  } else if (size === 'medium') {
    scale = 0.2;
  } else if (size === 'large') {
    scale = 0.3;
  }

  return new Style({
    image: new Icon({
      src: 'coverage.png',
      scale: scale,
      anchor: [0.5, 0.5],
    }),
  });
};
